import { Button, Space, Table, TableColumnsType, Tag } from 'antd';
import {
	ApplicationSummary,
	UserRole,
	Pagination,
	PagingConfig,
	ApplicationTableDataType,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useApplicationStatusTranslation } from '../../../hooks/useApplicationStatusTranslation';
import InCompleteCheck from '../../../assets/images/Incomplete-check.svg';
import CompleteCheck from '../../../assets/images/complete-check.svg';
import NotApprovedCross from '../../../assets/images/not-approved.svg';
import { BatchInfoState } from '../../../components/BatchInfo.store';
import { useSnapshot } from 'valtio';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { ApplicationStatus, ReviewAction } from '../../../shared/Constants';
import {
	hasPrivilege,
	isProposerSkippedToEnd,
	tablePaginationConfiguration,
} from '../../../shared/Utils';
import useUserSession from '../../../hooks/useUserSession';
import { PrivilegeCodes } from '../../../shared/RoleConstants';

interface ApplicationSummaryCardProps {
	applications: ApplicationSummary[] | undefined;
	onEvaluateClick: (application: ApplicationSummary) => void;
	onVerifyResultsClick: (application: ApplicationSummary) => void;
	onPageChange: (page: number, pageSize: number) => void;
	pageConfig: PagingConfig;
	pagination: Pagination;
}

export const ApplicationSummaryTableView = (
	props: ApplicationSummaryCardProps
) => {
	const {
		applications,
		onEvaluateClick,
		onVerifyResultsClick,
		onPageChange,
		pageConfig,
		pagination,
	} = props;
	const { getTranslatedValue } = useApplicationStatusTranslation();
	const { selectedRole, loggedInUserPrivileges } = useUserSession();
	const { t } = useTranslation();
	const batchStateSnap = useSnapshot(BatchInfoState);
	const batchInfo = batchStateSnap.batchInfo;

	const isZC = selectedRole === UserRole.ZC;
	const isCareTeam = selectedRole === UserRole.CARE_TEAM;

	const tablePaginationConfig = tablePaginationConfiguration(
		pageConfig,
		pagination,
		onPageChange
	);

	const handleVerifyResultsClick = (application: ApplicationTableDataType) => {
		onVerifyResultsClick(application.originalApplication);
	};

	const handleEvaluateClick = (application: ApplicationTableDataType) => {
		onEvaluateClick(application.originalApplication);
	};

	const showEvaluate = hasPrivilege(
		PrivilegeCodes.GET_APPLICATION,
		loggedInUserPrivileges
	);

	const showVerifyResults =
		(selectedRole === UserRole.ZC &&
			batchInfo?.snpResultDateForZC &&
			batchInfo.snpResultDateForZC !== '1970-01-01T00:00:00Z' &&
			moment().isSameOrAfter(batchInfo?.snpResultDateForZC)) ||
		(selectedRole === UserRole.APPROVER &&
			batchInfo?.snpResultDateForApprover &&
			batchInfo.snpResultDateForApprover !== '1970-01-01T00:00:00Z' &&
			moment().isSameOrAfter(batchInfo?.snpResultDateForApprover));

	const applicationTableData = applications?.map((application) => {
		let statusValue = '';
		let proposerNameValue = '';
		let proposerSRCMIDValue = '';
		const applicationStatus = application?.applicationStatus;
		statusValue = getTranslatedValue(application.applicationStatus) || '';
		const isWaitingForProposer =
			applicationStatus === ApplicationStatus.FUNCTIONARY_ASSIGNED;
		const isProposerFinalRecommendation =
			applicationStatus === ApplicationStatus.FUNCTIONARY_SUBMITTED;
		proposerNameValue = application.proposerName || '';
		proposerSRCMIDValue = application.proposerSrcmId || '';
		const forwardedToCareTeamBy = [
			{ key: 'applicantForwardedToCareTeam', label: 'applicant' },
			{ key: 'proposerForwardedToCareTeam', label: 'proposer' },
			{ key: 'approverForwardedToCareTeam', label: 'approver' },
			{ key: 'zcForwardedToCareTeam', label: 'zc' },
		]
			.filter(({ key }) => application?.[key as keyof typeof application])
			.map(({ label }) => t(label));

		let statusStyle = '';

		switch (applicationStatus) {
			case ApplicationStatus.APPROVED:
				statusStyle = 'bg-green-10 border border-solid border-green text-green';
				break;

			case ApplicationStatus.REJECTED:
				statusStyle =
					'bg-red-10 border border-solid border-red-200 text-red-200';
				break;

			default:
				statusStyle =
					'bg-orange-10 border border-solid border-orange text-orange';
				break;
		}

		const tagClassName = `${statusStyle} text-md rounded-full p-2`;

		const tableData: ApplicationTableDataType = {
			key: application.applicant.srcmid,
			fullName: `${application.applicant.firstName ?? ''} ${
				application.applicant.lastName ?? ''
			}`,
			idCard: application.applicant.srcmid,
			gender: application.applicant.gender ?? '',
			center: application.center.name ?? '',
			zone: application.center.zone,
			proposerName: proposerNameValue,
			proposerSRCMID: proposerSRCMIDValue,
			status: <Tag className={tagClassName}>{statusValue}</Tag>,
			...(isProposerFinalRecommendation
				? {}
				: {
						application: application.applicantAnswersReviewed ? (
							<img src={CompleteCheck} alt="Complete" className="w-5 h-5" />
						) : (
							<img src={InCompleteCheck} alt="Incomplete" className="w-5 h-5" />
						),
						preceptor: application.preceptorInterviewDone ? (
							<img src={CompleteCheck} alt="Complete" className="w-5 h-5" />
						) : (
							<img src={InCompleteCheck} alt="Incomplete" className="w-5 h-5" />
						),
						candidate: application.applicantInterviewDone ? (
							<img src={CompleteCheck} alt="Complete" className="w-5 h-5" />
						) : (
							<img src={InCompleteCheck} alt="Incomplete" className="w-5 h-5" />
						),
						family: application.familyMemberInterviewDone ? (
							<img src={CompleteCheck} alt="Complete" className="w-5 h-5" />
						) : (
							<img src={InCompleteCheck} alt="Incomplete" className="w-5 h-5" />
						),
						otherReference: application.otherReferenceProvided ? (
							<img
								src={
									application.otherReferenceInterviewDone
										? CompleteCheck
										: InCompleteCheck
								}
								alt="Check"
								className="w-5 h-5"
							/>
						) : (
							<>NA</>
						),
				  }),
			...(isWaitingForProposer
				? {}
				: {
						preceptorRecommendation: application.preceptorRecommendation
							?.review ? (
							<img
								src={
									application.preceptorRecommendation?.review ===
									ReviewAction.APPROVE
										? CompleteCheck
										: isProposerSkippedToEnd(
												application?.proposerRecommendation
										  )
										? InCompleteCheck
										: NotApprovedCross
								}
								alt="Incomplete Images"
								className="w-5 h-5"
							/>
						) : (
							<>
								<img
									src={InCompleteCheck}
									alt="Incomplete Images"
									className="w-5 h-5"
								/>
							</>
						),
						proposerRecommendation: application.proposerRecommendation
							?.review ? (
							<img
								src={
									application.proposerRecommendation?.review ===
									ReviewAction.APPROVE
										? CompleteCheck
										: isProposerSkippedToEnd(
												application?.proposerRecommendation
										  )
										? InCompleteCheck
										: NotApprovedCross
								}
								alt="Incomplete Images"
								className="w-5 h-5"
							/>
						) : (
							<>
								<img
									src={InCompleteCheck}
									alt="Incomplete Images"
									className="w-5 h-5"
								/>
							</>
						),
						ZCRecommendation: application.zcRecommendation?.review ? (
							<img
								src={
									application.zcRecommendation?.review ===
										ReviewAction.APPROVE ||
									application.zcRecommendation?.review ===
										ReviewAction.APPROVE_IDK_CANDIDATE
										? CompleteCheck
										: isProposerSkippedToEnd(
												application?.proposerRecommendation
										  )
										? InCompleteCheck
										: NotApprovedCross
								}
								alt="Incomplete Images"
								className="w-5 h-5"
							/>
						) : (
							<>
								<img
									src={InCompleteCheck}
									alt="Incomplete Images"
									className="w-5 h-5"
								/>
							</>
						),
						careTeamRecommendation: !application.shouldCareTeamReview ? (
							<>NA</>
						) : application.careTeamRecommendation?.review ? (
							<img
								src={
									application.careTeamRecommendation?.review ===
									ReviewAction.APPROVE
										? CompleteCheck
										: isProposerSkippedToEnd(
												application?.proposerRecommendation
										  )
										? InCompleteCheck
										: NotApprovedCross
								}
								alt="Incomplete Images"
								className="w-5 h-5"
							/>
						) : (
							<>
								<img
									src={InCompleteCheck}
									alt="Incomplete Images"
									className="w-5 h-5"
								/>
							</>
						),
				  }),
			forwardedToCareTeamBy: forwardedToCareTeamBy.length
				? forwardedToCareTeamBy.join(', ')
				: '-',
			originalApplication: application,
			applicationStatus,
		};

		return tableData;
	});

	const isWaitingForProposer = applicationTableData?.every(
		(row) => row.applicationStatus === ApplicationStatus.FUNCTIONARY_ASSIGNED
	);
	const isProposerFinalRecommendation = applicationTableData?.every(
		(row) => row.applicationStatus === ApplicationStatus.FUNCTIONARY_SUBMITTED
	);
	const isApproved = applicationTableData?.every(
		(row) => row.applicationStatus === ApplicationStatus.APPROVED
	);
	const isNotGoingForward = applicationTableData?.every(
		(row) => row.applicationStatus === ApplicationStatus.REJECTED
	);

	const showAllColumns = isApproved || isNotGoingForward;
	const hideInterviewColumns = isZC || isCareTeam;

	const baseColumns: TableColumnsType<ApplicationTableDataType> = [
		{
			title: t('fullName'),
			dataIndex: 'fullName',
			key: 'fullName',
			className:
				'max-w-[200px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
			render: (text: string, record: ApplicationTableDataType) => {
				if (showVerifyResults) {
					return (
						<span
							className="text-blue-500 cursor-pointer hover:underline max-w-[400px] whitespace-nowrap md:whitespace-normal break-words"
							onClick={() => handleVerifyResultsClick(record)}
						>
							{text}
						</span>
					);
				} else {
					return (
						<span
							className="text-blue-500 cursor-pointer hover:underline"
							onClick={() => handleEvaluateClick(record)}
						>
							{text}
						</span>
					);
				}
			},
		},
		{
			title: t('idCard'),
			dataIndex: 'idCard',
			key: 'idCard',
		},
		{
			title: t('gender'),
			dataIndex: 'gender',
			key: 'gender',
			render: (__, record, index) => (
				<span key={index}>
					<span className="ml-2">
						{record?.gender
							? record.gender.charAt(0).toUpperCase() +
							  record.gender.slice(1).toLowerCase()
							: ''}
					</span>
				</span>
			),
		},
		{
			title: t('center'),
			dataIndex: 'center',
			key: 'center',
			className: 'whitespace-nowrap',
		},
		{
			title: t('zone'),
			dataIndex: 'zone',
			key: 'zone',
			className:
				'max-w-[400px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
		},
		{
			title: t('proposerName'),
			dataIndex: 'proposerName',
			key: 'proposerName',
			width: 150,
			className: 'whitespace-nowrap',
		},
		{
			title: t('proposerSRCMID'),
			dataIndex: 'proposerSRCMID',
			key: 'proposerSRCMID',
		},
	];

	if (!hideInterviewColumns) {
		if (isWaitingForProposer || showAllColumns) {
			baseColumns.push(
				{
					title: t('application'),
					dataIndex: 'application',
					key: 'application',
					align: 'center',
				},
				{
					title: t('candidate'),
					dataIndex: 'candidate',
					key: 'candidate',
					align: 'center',
				},
				{
					title: t('preceptor'),
					dataIndex: 'preceptor',
					key: 'preceptor',
					align: 'center',
				},
				{
					title: t('family'),
					dataIndex: 'family',
					key: 'family',
					align: 'center',
				},
				{
					title: t('otherReference'),
					dataIndex: 'otherReference',
					key: 'otherReference',
					align: 'center',
				}
			);
		}

		if (isProposerFinalRecommendation || showAllColumns) {
			baseColumns.push(
				{
					title: t('preceptorRecommendation'),
					dataIndex: 'preceptorRecommendation',
					key: 'preceptorRecommendation',
					align: 'center',
				},
				{
					title: t('proposerRecommendation'),
					dataIndex: 'proposerRecommendation',
					key: 'proposerRecommendation',
					align: 'center',
				},
				{
					title: t('ZCRecommendation'),
					dataIndex: 'ZCRecommendation',
					key: 'ZCRecommendation',
					align: 'center',
				},
				{
					title: t('careTeamRecommendation'),
					dataIndex: 'careTeamRecommendation',
					key: 'careTeamRecommendation',
					align: 'center',
				}
			);
		}
	}

	if (isCareTeam && Array.isArray(baseColumns) && baseColumns.length >= 2) {
		baseColumns.splice(2, 0, {
			title: t('forwardedToCareTeamBy'),
			dataIndex: 'forwardedToCareTeamBy',
			key: 'forwardedToCareTeamBy',
			align: 'center',
		});
	}

	if (showEvaluate) {
		baseColumns.push({
			title: t('action'),
			key: 'operation',
			fixed: 'right',
			width: 150,
			render: (__, record) => (
				<Space size="middle" className="whitespace-nowrap">
					<Button
						type="primary"
						onClick={() => handleEvaluateClick(record)}
						className="w-fit"
					>
						{showAllColumns ? t('view') : t('evaluate')}
					</Button>
				</Space>
			),
		});
	}

	const columns = [...baseColumns];
	if (!isCareTeam) {
		columns.splice(2, 0, {
			title: t('status'),
			dataIndex: 'status',
			key: 'status',
		});
	}

	return (
		<>
			<Table
				columns={columns}
				dataSource={applicationTableData}
				pagination={tablePaginationConfig}
				scroll={{ x: 'max-content' }}
				rowClassName={() => 'border-b border-gray-300 bg-white'}
				className="w-full overflow-x-auto !border-separate !border-spacing-y-0"
			/>
		</>
	);
};
